import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "../../lib/api";
import DiscoverLayout from "../../components/discover/DiscoverLayout";
import StartupCard from "../../components/discover/StartupCard";
import SearchBar from "../../components/discover/SearchBar";
import FilterBar from "../../components/discover/FilterBar";
import Pagination from "../../components/discover/Pagination";

export default function StartupsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const [activeFilters, setActiveFilters] = useState({
    industry: "All",
    location: "All",
    stage: "All",
    model: "All",
    year: "All",
  });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/discover/startups?limit=100")
      .then((r) => {
        if (alive) {
          setItems(r.data.items || []);
        }
      })
      .catch((e) => console.error("Error loading startups:", e))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  const handleFilterChange = (key, value) => {
    setActiveFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Determine unique filter options from loaded data
  const getFilterOptions = (key, dataField) => {
    const vals = items
      .map((item) => {
        if (key === "model") {
          // Mock or derive model if not present, e.g. SaaS if industry is SaaS/Enterprise
          if (item.industry === "SaaS" || item.industry === "Enterprise") return "SaaS";
          if (item.industry === "Fintech") return "B2B";
          if (item.industry === "E-commerce") return "Marketplace";
          return "B2C";
        }
        if (key === "location") {
          return item.location ? item.location.split(",")[0].trim() : "";
        }
        if (key === "stage") {
          return item.stage ? item.stage.replace(/\s*\(Unicorn\)\s*/i, "").trim() : "";
        }
        return item[dataField];
      })
      .filter(Boolean);
    return Array.from(new Set(vals)).sort();
  };

  const industriesList = getFilterOptions("industry", "industry");
  const locationsList = getFilterOptions("location", "location");
  const stagesList = getFilterOptions("stage", "stage");
  const modelsList = ["B2B", "B2C", "SaaS", "Marketplace", "D2C"];
  const yearsList = getFilterOptions("year", "founded_year").map(String);

  const filters = [
    { key: "industry", label: "Industry", options: industriesList },
    { key: "location", label: "Location", options: locationsList },
    { key: "stage", label: "Funding Stage", options: stagesList },
    { key: "model", label: "Business Model", options: modelsList },
    { key: "year", label: "Year Founded", options: yearsList },
  ];

  // Apply filters and search query
  const filtered = items.filter((item) => {
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      item.name.toLowerCase().includes(q) ||
      (item.description && item.description.toLowerCase().includes(q)) ||
      (item.industry && item.industry.toLowerCase().includes(q));

    const matchesIndustry =
      activeFilters.industry === "All" || item.industry === activeFilters.industry;

    const matchesLocation =
      activeFilters.location === "All" ||
      (item.location && item.location.toLowerCase().includes(activeFilters.location.toLowerCase()));

    const matchesStage =
      activeFilters.stage === "All" ||
      (item.stage && item.stage.toLowerCase().includes(activeFilters.stage.toLowerCase()));

    const derivedModel =
      item.industry === "SaaS" || item.industry === "Enterprise"
        ? "SaaS"
        : item.industry === "Fintech"
        ? "B2B"
        : item.industry === "E-commerce"
        ? "Marketplace"
        : "B2C";
    const matchesModel = activeFilters.model === "All" || derivedModel === activeFilters.model;

    const matchesYear =
      activeFilters.year === "All" || String(item.founded_year) === activeFilters.year;

    return matchesSearch && matchesIndustry && matchesLocation && matchesStage && matchesModel && matchesYear;
  });

  // Pagination
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <DiscoverLayout
      title="Indian Startups"
      subtitle="Discover high-growth startups founded and headquartered in India. Browse through sector-disrupting technology leaders."
    >
      <div className="space-y-6">
        {/* Search & Stats */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="w-full md:w-96">
            <SearchBar
              value={searchQuery}
              onChange={(q) => {
                setSearchQuery(q);
                setCurrentPage(1);
              }}
              placeholder="Search startups by name, description, industry..."
            />
          </div>
          <span className="text-xs text-gray-500 font-bold">
            Showing {filtered.length} startups
          </span>
        </div>

        {/* Filters */}
        <FilterBar
          filters={filters}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
        />

        {/* Startups Grid */}
        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
            <p className="text-sm text-gray-500 font-medium">No startups match your search/filter criteria.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedItems.map((c) => (
                <StartupCard key={c.slug} c={c} />
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </DiscoverLayout>
  );
}
